import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * ARC Portal Kit's `.pk-stat-row` metric-card pattern: one gradient ramp
 * sliced across a row of cards via hardcoded `nth-child` CSS, so a row must
 * contain exactly 4 cards (or exactly 3 with the `variant="3"` row class) for
 * the ramp to render correctly - chunk a longer list of metrics into multiple
 * `<StatRow>`s rather than one long row.
 */
export function StatRow({ variant, children }: { variant?: '3' | '4'; children: React.ReactNode }) {
  return <div className={`pk-stat-row ${variant === '3' ? 'pk-stat-row-3' : ''}`}>{children}</div>;
}

interface StatProps {
  icon?: React.ReactNode;
  title: string;
  value: React.ReactNode;
  unit?: string;
  subtitle?: string;
  to?: string;
  progress?: { used: number; total: number };
}

export function Stat({ icon, title, value, unit, subtitle, to, progress }: StatProps) {
  const navigate = useNavigate();
  const hasProgress = progress && progress.total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((progress!.used / progress!.total) * 100)) : 0;

  return (
    <div
      className={`pk-stat ${to ? 'pk-stat-link' : ''}`}
      onClick={to ? () => navigate(to) : undefined}
      style={to ? { cursor: 'pointer' } : undefined}
    >
      {icon && <div className="pk-i-plate">{icon}</div>}
      <span className="pk-label">{title}</span>
      <span className="val">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </span>
      {subtitle && <span className="foot">{subtitle}</span>}

      {hasProgress && (
        <div className="pk-progress" style={{ marginTop: 2 }}>
          <div style={{ height: 5, borderRadius: 999, background: 'var(--arc-line-2)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                borderRadius: 999,
                width: `${pct}%`,
                background: pct >= 90 ? 'var(--arc-danger)' : pct >= 70 ? 'var(--arc-warning)' : 'var(--arc-accent)',
                transition: 'width var(--arc-t) var(--arc-ease)',
              }}
            />
          </div>
          <span className="pk-small" style={{ display: 'block', marginTop: 4 }}>{pct}% used</span>
        </div>
      )}

      {to && <ChevronRight size={16} className="go" />}
    </div>
  );
}

export function StatSkeleton() {
  return (
    <div className="pk-stat">
      <div className="pk-skel" style={{ width: 30, height: 30, borderRadius: 'var(--arc-r-sm)' }} />
      <div className="pk-skel pk-skel-text" style={{ width: '45%' }} />
      <div className="pk-skel pk-skel-title" style={{ width: '65%' }} />
      <div className="pk-skel pk-skel-text" style={{ width: '55%' }} />
    </div>
  );
}
